import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Row,
  Button,
  Form,
  Col,
  Spinner
} from '@themesberg/react-bootstrap';
import { X, Plus } from 'lucide-react';
import Select from 'react-select';
import { useDispatch, useSelector } from 'react-redux';
import { arrayToDropdownOptions } from '../../utils/sms/arrayToDropdownOptions';
import EditableDropDown from '../../components/common/EditableDropdown';
import { debounce } from '../../utils/debounce';
import AsyncDropdown from '../../components/AsyncDropdown';
import { t } from 'i18next';
import { apiCall } from '../../middlewares/api';
import { updateSpinnerState } from '../../actions/spinner';
import { updateToastInfo } from '../../actions/settings';
import { apiCallConsts } from '../../constants/apiCallConsts';
import { setProductsMapInStore } from '../../actions/oms/allProducts';
import ConfirmationModal from '../../components/common/confirmationModal';
import * as XLSX from 'xlsx';
import InfoModal from '../oms/inventory/components/InfoModal';
import ExcelDownloadReport from '../../components/common/excel/download';
import ExportButton from '../oms/orders/components/ExportButton';

// Helper function for common input style.
const getInputStyle = (error) => ({
  backgroundColor: '#f8f9fa',
  borderRadius: '0px',
  border: error ? '1px solid red' : '1px solid #dee2e6'
});

// A dedicated input component for the Exchange Bonus field.
// This version sets the input field's background to green if the field has been edited.
const ExcelBonusInput = ({ value, row, onValueChange }) => {
  const [inputValue, setInputValue] = useState(value);

  // Update local state if the prop changes externally.
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const handleChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleBlur = () => {
    onValueChange(row, inputValue);
  };

  // Build the style object.
  const inputStyle = { width: '100%', ...getInputStyle(false) };
  if (row.edited) {
    inputStyle.backgroundColor = '#90ee90';
  }

  return (
    <input
      type="number"
      value={inputValue}
      onChange={handleChange}
      onBlur={handleBlur}
      style={inputStyle}
    />
  );
};

const CreateNewTemplate = () => {
  const [searchParams] = useSearchParams();
  const templateId = searchParams.get('id'); // e.g. ?id=123
  const isCloning = searchParams.get('clone') === 'true';
  const isEditMode = Boolean(templateId) && !isCloning;
  
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // -------------------------------------------------------------
  // API calls
  const fetchTemplateData = async (templateId) => {
    try {
      dispatch(updateSpinnerState(true));
      const response = await apiCall(apiCallConsts?.GET_METHOD, 'internal/template/' + templateId);
      dispatch(updateSpinnerState(false));
      return response?.data;
    } catch (e) {
      dispatch(updateSpinnerState(false));
      dispatch(
        updateToastInfo({
          show: true,
          type: 'danger',
          title: 'Failed!',
          message: t(e.message)
        })
      );
      return null;
    }
  };

  const fetchAvailableBuyBackSkus = async () => {
    // Simulated API data; adjust as needed.
    return Promise.resolve([
      { id: '227945', name: 'SCRAP OLD AC' },
      { id: '227941', name: 'SCRAP OLD FRIDGE SD' },
      { id: '227994', name: 'SCRAP OLD FRIDGE DD' },
      { id: '234452', name: 'SCRAP OLD ONLINE FR' },
      { id: '227974', name: 'SCRAP OLD FRIDGE SIDE BY SIDE' },
      { id: '227952', name: 'SCRAP OLD FRIDGE ABOVE 350 LTR' },
      { id: '227989', name: 'SCRAP OLD FRIDGE 100 & BELOW 100 LTR' },
      { id: '227915', name: 'SCRAP OLD WM AUTO' },
      { id: '227870', name: 'SCRAP OLD WM SEMI' },
      { id: '227895', name: 'SCRAP OLD WM FRONT LODING' },
      { id: '227920', name: 'SCRAP OLD DRYERS' },
      { id: '227884', name: 'SCRAP OLD DISH WASHERS' },
      { id: '234453', name: 'SCRAP OLD ONLINE WM' },
      { id: '227965', name: 'SCRAP OLD MICROWAVE' },
      { id: '231228', name: 'SCRAP OLD BAJAJ MIXER' },
      { id: '230752', name: 'SCRAP OLD BAJAJ GLAMORE' },
      { id: '227948', name: 'SCRAP OLD KENSTAR MIXER' },
      { id: '227983', name: 'SCRAP OLD MIXER GX3701' },
      { id: '227996', name: 'SCRAP OLD MIXER MORPHY RICHARDS' },
      { id: '227873', name: 'SCRAP OLD  BAJAJ HERCULO' },
      { id: '227914', name: 'SCRAP OLD PREETHI MIXER' },
      { id: '227898', name: 'SCRAP OLD BAJAJ VIRTUE' },
      { id: '236002', name: 'SCRAP OLD EF GLAM RO' },
      { id: '227860', name: 'SCRAP OLD PREETHI MIXER SUPREME' },
      { id: '234879', name: 'SCRAP OLD FRIDGE ABOVE 450 LTR' },
      { id: '234911', name: 'SCRAP PREETHI MIXER ZODIAC' },
      { id: '227999', name: 'SCRAP OLD  BAJAJ NINJA SERIES VIRTUE' },
      { id: '230751', name: 'SCRAP OLD BAJAJ EVOQUE' },
      { id: '234912', name: 'SCRAP PREETHI MIXER SUPREME' },
      { id: '227900', name: 'SCRAP OLD FABER COOKTOP' },
      { id: '230753', name: 'SCRAP OLD BAJAJ  FINESSE' },
      { id: '237182', name: 'SCRAP OLD PRESTIGE MG STYLO' },
      { id: '237184', name: 'SCRAP OLD PRESTIGE ENDURA 1000 WATTS' },
      { id: '229449', name: 'SCRAP OLD MORPHY RICHARDS SUPREME' },
      { id: '227949', name: 'SCRAP OLD  BAJAJ MIXER GRINDER HERCULO' },
      { id: '231231', name: 'SCRAP OLD BAJAJ MIXER  HERCULO' },
      { id: '237185', name: 'SCRAP OLD PRESTIGE ENDURA 3 JAR' },
      { id: '227883', name: 'SCRAP OLD LED BELOW 32' },
      { id: '227984', name: 'SCRAP OLD LED 32 WORKING SSL' },
      { id: '227962', name: 'SCRAP OLD LED 55 TO 65 SSL' },
      { id: '227887', name: 'SCRAP OLD LED 32 OTHER BRAND' },
      { id: '227943', name: 'SCRAP OLD LED 40 TO 50 SSL WORKING' },
      { id: '227918', name: 'SCRAP OLD LED 32 SSL' },
      { id: '227882', name: 'SCRAP OLD LED 32 WORKING OTHER BRAND' },
      { id: '227957', name: 'SCRAP OLD LED 40 TO 50 SSL' },
      { id: '227894', name: 'SCRAP OLD LED 55 TO 65 WORKING' },
      { id: '227861', name: 'SCRAP OLD LED 55 TO 65 SSL WORKING' },
      { id: '227997', name: 'SCRAP OLD LED 40 TO 50' },
      { id: '227889', name: 'SCRAP OLD LED 55 TO 65' },
      { id: '227933', name: 'SCRAP OLD LED ABOVE 70' },
      { id: '227859', name: 'SCRAP OLD LED 40 TO 50 WORKING' },
      { id: '227968', name: 'SCRAP OLD LED ABOVE 70 SSL' },
      { id: '227976', name: 'SCRAP OLD ANY TELEVISION' },
      { id: '227928', name: 'SCRAP OLD ONLINEAC' },
      { id: '234452', name: 'SCRAP OLD ONLINE FR' },
      { id: '227915', name: 'SCRAP OLD WM AUTO' },
      { id: '227870', name: 'SCRAP OLD WM SEMI' },
      { id: '227895', name: 'SCRAP OLD WM FRONT LOADING' },
      { id: '240699', name: 'SCRAP OLD LED 32' },
      { id: '240700', name: 'SCRAP OLD LED 40 TO 43' },
      { id: '240701', name: 'SCRAP OLD LED 50 TO 55' },
      { id: '227889', name: 'SCRAP OLD LED 55 TO 65' },
    ]);
  };

  const fetchAvailableQuestions = async () => {
    try {
      dispatch(updateSpinnerState(true));
      const response = await apiCall(apiCallConsts?.GET_METHOD, 'internal/questions');
      dispatch(updateSpinnerState(false));
      return response?.data || [];
    } catch (e) {
      dispatch(updateSpinnerState(false));
      dispatch(
        updateToastInfo({
          show: true,
          type: 'danger',
          title: 'Failed!',
          message: t(e.message)
        })
      );
      return [];
    }
  };

  // -------------------------------------------------------------
  // Local state for dropdowns and products
  const [vanNo, setVanNo] = useState([]);
  const [category, setCategory] = useState([]);
  const [brand, setBrand] = useState([]);
  const productsMap = useSelector((state) => state?.allProducts?.productsMap || {});
  const companies = useSelector((state) => state?.allProducts?.companies || []);
  const categories = useSelector((state) => state?.allProducts?.categories || {});
  
  const vanNoOptions = useMemo(
    () => arrayToDropdownOptions(Object.keys(productsMap)),
    [productsMap]
  );

  // For creation mode, define it as neither edit nor clone.
  const isCreation = !isEditMode && !isCloning;
  
  const fetchOptions = useCallback(
    debounce(async (inputValue, options) => {
      if (inputValue.length > 2) {
        try {
          const filteredOptions = options?.filter((product) =>
            product.value.toLowerCase().includes(inputValue.toLowerCase())
          );
          return filteredOptions;
        } catch (e) {
          console.log(e);
        }
      }
    }),
    []
  );

  // -------------------------------------------------------------
  // Template Details
  const [templateName, setTemplateName] = useState('');
  const [description, setDescription] = useState('');

  // -------------------------------------------------------------
  // Questions (for template)
  const [availableQuestions, setAvailableQuestions] = useState([]);
  // Selected questions are stored as question IDs.
  const [selectedQuestions, setSelectedQuestions] = useState(['']);

  useEffect(() => {
    fetchAvailableQuestions().then(setAvailableQuestions);
  }, []);

  // -------------------------------------------------------------
  // Products loading
  const fetchProducts = async () => {
    try {
      dispatch(updateSpinnerState(true));
      const productsMap = await apiCall(apiCallConsts.GET_METHOD, 'internal/products/all?isOnlyVanNo=true');
      dispatch(setProductsMapInStore(productsMap));
      dispatch(updateSpinnerState(false));
    } catch (e) {
      dispatch(updateSpinnerState(false));
      dispatch(
        updateToastInfo({
          show: true,
          type: 'danger',
          title: 'Failed!',
          message: t(e.message)
        })
      );
    }
  };

  useEffect(() => {
    if (Object.keys(productsMap).length === 0) {
      fetchProducts();
    }
  }, [productsMap]);

  // Build react-select options for questions.
  const questionOptions = availableQuestions.map((qObj) => ({
    value: qObj.objectId,
    label: qObj.text,
    answerOptions: qObj.answerOptions || []
  }));

  const handleQuestionChange = (index, selectedOption) => {
    const newSelected = [...selectedQuestions];
    newSelected[index] = selectedOption ? selectedOption.value : '';
    setSelectedQuestions(newSelected);
  };

  const handleAddQuestion = () => setSelectedQuestions([...selectedQuestions, '']);
  const handleRemoveQuestion = (index) =>
    setSelectedQuestions(selectedQuestions.filter((_, i) => i !== index));

  // -------------------------------------------------------------
  // Dynamic Exchange Configurations Section
  const [exchangeConfigRows, setExchangeConfigRows] = useState([
    { answers: {}, exchangeValue: '', exchangeBonus: '', buyBackSku: '' }
  ]);

  const activeQuestions = selectedQuestions.filter((q) => q !== '');
  const showExchangeConfig = activeQuestions.length >= 3;

  const updateExchangeConfigRow = (rowIndex, key, value) => {
    const updatedRows = [...exchangeConfigRows];
    const answers = updatedRows[rowIndex].answers || {};
    if (key in answers) {
      answers[key] = value;
      updatedRows[rowIndex].answers = answers;
    } else {
      updatedRows[rowIndex][key] = value;
    }
    setExchangeConfigRows(updatedRows);

    // Remove error if value is valid.
    if ((key === 'exchangeValue' || key === 'buyBackSku') && value.trim()) {
      setErrors((prev) => {
        const updatedErrors = { ...prev };
        delete updatedErrors[`exchangeRow${rowIndex}_${key}`];
        return updatedErrors;
      });
    }
  };

  // -------------------------------------------------------------
  // Additional Exchange Details fields (if any)
  const [exchangeValueGlobal, setExchangeValueGlobal] = useState('');
  const [exchangeBonusGlobal, setExchangeBonusGlobal] = useState('');
  const [exchangeNote, setExchangeNote] = useState('');

  // -------------------------------------------------------------
  // Dropdowns for creation: Brands, VanNo, etc.
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [availableBuyBackSkus, setAvailableBuyBackSkus] = useState([]);

  useEffect(() => {
    fetchAvailableBuyBackSkus().then((data) =>
      setAvailableBuyBackSkus(data.map((item) => ({ value: item.id, label: item.name })))
    );
  }, [isCreation]);

  // -------------------------------------------------------------
  // Assigned Products Section (for edit/clone mode)
  const [assignedProducts, setAssignedProducts] = useState([]);
  const [newAssignedProduct, setNewAssignedProduct] = useState(null);

  const handleAddAssignedProduct = () => {
    if (newAssignedProduct && !assignedProducts.includes(newAssignedProduct.value)) {
      setAssignedProducts([...assignedProducts, newAssignedProduct.value]);
      setNewAssignedProduct(null);
    }
  };

  // -------------------------------------------------------------
  // Error state for validations
  const [errors, setErrors] = useState({});

  // -------------------------------------------------------------
  // Modal confirmation and loading state
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  // -------------------------------------------------------------
  // Excel Upload State & Modal for showing full uploaded data
  const [excelData, setExcelData] = useState([]);
  const [showExcelModal, setShowExcelModal] = useState(false);

  // New state for vanNo search filter.
  const [vanSearch, setVanSearch] = useState('');

  // Derived filtered Excel data based on the vanNo search term.
  const filteredExcelData = useMemo(() => {
    return excelData.filter((item) =>
      String(item.vanNo).toLowerCase().includes(vanSearch.toLowerCase())
    );
  }, [excelData, vanSearch]);

  // Handler to process the uploaded Excel file.
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const binaryStr = evt.target.result;
      const workbook = XLSX.read(binaryStr, { type: 'binary' });
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      // Convert sheet to JSON
      const uploadedData = XLSX.utils.sheet_to_json(worksheet, { defval: '' });
      // Normalize the data into objects with vanNo and exchangeBonus,
      // and store the original bonus to compare later.
      const normalizedData = uploadedData.map((row) => {
        const bonus = row.exchangeBonus || row.EXCHANGEBONUS || row.exchange_bonus || '';
        return {
          vanNo: row.vanNo || row.VANNO || row.van_no || '',
          exchangeBonus: bonus,
          originalExchangeBonus: bonus,
          edited: false
        };
      });

      if (excelData && excelData.length > 0) {
        // Merge new data with existing API data without duplications.
        const vanNoObj = {};
        const updatedData = [];
        [...normalizedData, ...excelData].forEach((item) => {
          if (!vanNoObj[item?.vanNo]) {
            vanNoObj[item?.vanNo] = item;
            updatedData.push(item);
          }
        });
        setExcelData(updatedData);
      } else {
        if (normalizedData.length > 0) {
          setExcelData(normalizedData);
        }
      }
    };
    reader.readAsBinaryString(file);
  };

  // Function to download the Excel data as a CSV file.
  const downloadExcelData = () => {
    if (!excelData || excelData.length === 0) return;
    const header = ['vanNo', 'exchangeBonus'];
    const csvRows = [header.join(',')];

    excelData.forEach((row) => {
      csvRows.push([row.vanNo, row.exchangeBonus].join(','));
    });

    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'exchange_bonus_data.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // -------------------------------------------------------------
  // Load API data for Edit/Clone mode
  useEffect(() => {
    if (templateId) {
      dispatch(updateSpinnerState(true));
      fetchTemplateData(templateId)
        .then((data) => {
          if (!data) return;
          setTemplateName(isCloning ? `${data.templateName} (Cloned)` : data.templateName);
          setDescription(data.text || '');
          setExchangeValueGlobal(data.exchangeValue || '');
          setExchangeBonusGlobal(data.exchangeBonus || '');
          setVanNo(
            data?.products?.map((each) => ({ label: each, value: each })) || []
          );
          setCategory(data?.categorys || []);
          setBrand(data?.brands || []);
          if (data.questionArrayIds && data.questionArrayIds.length > 0) {
            setSelectedQuestions(data.questionArrayIds);
          } else if (data.exchangeRules && data.exchangeRules.length > 0) {
            const questionKeys = Object.keys(data.exchangeRules[0]).filter(
              (key) =>
                key !== 'exchangeValue' &&
                key !== 'exchangeBonus' &&
                key !== 'buyBackVanNo' &&
                key !== 'title'
            );
            setSelectedQuestions(questionKeys);
          }
          if (data.exchangeRules && data.exchangeRules.length > 0) {
            const normalizedRules = data.exchangeRules.map((rule) => {
              if (!rule.answers) {
                const answers = Object.keys(rule).reduce((acc, key) => {
                  if (
                    key !== 'exchangeValue' &&
                    key !== 'exchangeBonus' &&
                    key !== 'buyBackVanNo' &&
                    key !== 'title'
                  ) {
                    acc[key] = rule[key] || '';
                  }
                  return acc;
                }, {});
                return {
                  answers,
                  exchangeValue: rule.exchangeValue || '',
                  exchangeBonus: rule.exchangeBonus || '',
                  buyBackSku: rule.buyBackVanNo || ''
                };
              }
              return {
                answers: rule.answers || {},
                exchangeValue: rule.exchangeValue || '',
                exchangeBonus: rule.exchangeBonus || '',
                buyBackSku: rule.buyBackVanNo || ''
              };
            });
            setExchangeConfigRows(normalizedRules);
          }
          // If the API returns Excel data, assume it is stored in data.excelData.
          // Otherwise, Excel data will be updated via file upload.
          if (data.vanNoBonus && data.vanNoBonus.length > 0) {
            setExcelData(data.vanNoBonus);
          }
        })
        .catch((error) => {
          dispatch(
            updateToastInfo({
              show: true,
              type: 'danger',
              title: 'Failed!',
              message: t(error.message)
            })
          );
        })
        .finally(() => dispatch(updateSpinnerState(false)));
    }
  }, [templateId, isCloning, isCreation, dispatch]);

  // -------------------------------------------------------------
  // Save Template function (create/update)
  const saveTemplate = async () => {
    const exchangeRules = exchangeConfigRows.map((row) => {
      const rule = {};
      activeQuestions.forEach((qId) => {
        rule[qId] = (row.answers && row.answers[qId]) || '';
      });
      rule.exchangeValue = row.exchangeValue;
      rule.exchangeBonus = row.exchangeBonus;
      rule.buyBackVanNo = row.buyBackSku;
      if (row.buyBackSku) {
        const sku = availableBuyBackSkus.find((s) => s.value === row.buyBackSku);
        rule.title = sku ? sku.label : '';
      } else {
        rule.title = '';
      }
      return rule;
    });

    const payload = {
      name: templateName,
      question: selectedQuestions.filter((q) => q !== ''),
      exchangeRules,
      products: vanNo?.length > 0 ? vanNo.map((each) => each?.value) : [],
      category: category?.length > 0 ? category : [],
      brands: brand ? brand : [],
      vanNoBonus : excelData // Pass current Excel data if needed.
    };

    try {
      dispatch(updateSpinnerState(true));
      if (isEditMode) {
        await apiCall(apiCallConsts?.PUT_METHOD, `internal/template/${templateId}`, payload);
      } else {
        await apiCall(apiCallConsts?.POST_METHOD, 'internal/template', payload);
      }
      dispatch(updateSpinnerState(false));
      navigate(-1);
    } catch (e) {
      dispatch(updateSpinnerState(false));
      dispatch(
        updateToastInfo({
          show: true,
          type: 'danger',
          title: 'Failed!',
          message: t(e.message)
        })
      );
    }
  };

  // -------------------------------------------------------------
  // Overall Action Handler & Validations
  const handleSave = () => {
    const newErrors = {};

    if (!templateName.trim()) {
      newErrors.templateName = 'Template name cannot be empty.';
    }

    const activeQs = selectedQuestions.filter((q) => q !== '');
    if (activeQs.length < 3) {
      newErrors.questions = 'Please select at least 3 questions.';
    }

    if (showExchangeConfig) {
      exchangeConfigRows.forEach((row, i) => {
        if (!(row.exchangeValue || '').trim()) {
          newErrors[`exchangeRow${i}_exchangeValue`] = `Exchange Value is required in exchange config row ${i + 1}.`;
        }
        if (!(row.buyBackSku || '').trim()) {
          newErrors[`exchangeRow${i}_buyBackSku`] = `BuyBack SKU is required in exchange config row ${i + 1}.`;
        }
      });
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setShowConfirm(true);
  };

  const handleCancel = () => navigate(-1);
  const handleAssignNow = () => navigate('/templete/assign');

  // Layout helper styles
  const leftStyle = { width: '20%', fontWeight: 600, padding: '8px' };
  const rightStyle = { width: '80%', padding: '16px' };

  if (loading) {
    return (
      <Container fluid style={{ minHeight: '100vh', padding: '24px', textAlign: 'center' }}>
        <Spinner animation="border" role="status" />
        <p style={{ marginTop: 12 }}>Loading template data...</p>
      </Container>
    );
  }

  return (
    <Container fluid style={{ backgroundColor: '#f8f9fa', minHeight: '100vh', padding: '24px' }}>
      {/* Heading Section */}
      <Row style={{ marginBottom: '16px' }}>
        <h3 style={{ fontWeight: 500, margin: 0 }}>
          Exchange Questions &gt;{' '}
          {isCloning ? 'Clone Template' : isEditMode ? 'Edit Template' : 'Create New Template'}
        </h3>
      </Row>

      {/* Template Details Section */}
      <Row style={{ marginBottom: '16px', paddingInline: '5px', paddingBlock: '16px', borderRadius: '4px', backgroundColor: '#ffffff' }}>
        <h6 style={{ fontWeight: 500, margin: 0 }}>Fill in the Details</h6>
      </Row>
      <Row style={{ backgroundColor: '#ffffff', marginBottom: '16px', padding: '5px' }}>
        <div style={leftStyle}>Template Details</div>
        <div style={rightStyle}>
          <Form.Group style={{ marginBottom: '12px' }}>
            <Form.Label>Template Name *</Form.Label>
            <Form.Control
              type="text"
              placeholder="Example: 'Laptop Exchange Template'"
              value={templateName}
              onChange={(e) => {
                setTemplateName(e.target.value);
                if (errors.templateName) {
                  setErrors({ ...errors, templateName: null });
                }
              }}
              style={getInputStyle(errors.templateName)}
            />
            {errors.templateName && (
              <Form.Text style={{ color: 'red' }}>{errors.templateName}</Form.Text>
            )}
          </Form.Group>
          <Form.Group>
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="Example: 'This template is used for laptop-related exchange questions.'"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              style={getInputStyle(false)}
            />
          </Form.Group>
        </div>
      </Row>

      {/* Select Questions Section */}
      <Row style={{ backgroundColor: '#ffffff', marginBottom: '16px', padding: '5px' }}>
        <div style={leftStyle}>Select Questions</div>
        <div style={rightStyle}>
          {errors.questions && (
            <Form.Text style={{ color: 'red', marginBottom: '8px', display: 'block' }}>
              {errors.questions}
            </Form.Text>
          )}
          {selectedQuestions.map((sel, index) => {
            const currentValue = questionOptions.find((opt) => opt.value === sel) || null;
            const filteredOptions = questionOptions.filter(
              (opt) => currentValue?.value === opt.value || !selectedQuestions.includes(opt.value)
            );
            return (
              <div key={index} style={{ marginBottom: '8px', display: 'flex', alignItems: 'center' }}>
                <div style={{ flex: 1 }}>
                  <Select
                    options={filteredOptions}
                    value={currentValue}
                    isClearable={true}
                    onChange={(option) => handleQuestionChange(index, option)}
                    placeholder="Select a question"
                  />
                </div>
                <Button variant="light" onClick={() => handleRemoveQuestion(index)} style={{ marginLeft: '8px' }}>
                  <X size={16} />
                </Button>
              </div>
            );
          })}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="light" onClick={handleAddQuestion} style={{ border: '0px', width: '200px' , borderRadius:"0px" }}>
              <Plus size={16} className="me-1" /> Add a Question
            </Button>
          </div>
        </div>
      </Row>

      {/* Dynamic Exchange Configurations Section */}
      {showExchangeConfig && (
        <Row style={{ backgroundColor: '#ffffff', marginBottom: '16px', padding: '5px' }}>
          <div style={leftStyle}>Exchange Configurations</div>
          <div style={rightStyle}>
            <Row className="mb-3" style={{ fontWeight: 600 }}>
              {activeQuestions.map((_, idx) => (
                <Col key={idx}>{`Q${idx + 1}`}</Col>
              ))}
              <Col>Exchange Value *</Col>
              <Col>Exchange Bonus</Col>
              <Col>BuyBack SKU *</Col>
              <Col xs="auto" />
            </Row>
            {exchangeConfigRows.map((row, rowIndex) => (
              <Row key={rowIndex} className="mb-3" style={{ padding: '8px', border: '1px solid #dee2e6', marginBottom: '16px' }}>
                {activeQuestions.map((qId, qIndex) => (
                  <Col key={qIndex}>
                    <Select
                      isClearable={true}
                      options={
                        (() => {
                          const qOption = questionOptions.find((opt) => opt.value === qId);
                          if (qOption && Array.isArray(qOption.answerOptions)) {
                            return qOption.answerOptions.map((opt) => ({
                              value: opt,
                              label: opt
                            }));
                          }
                          return [];
                        })()
                      }
                      value={
                        row.answers && row.answers[qId]
                          ? { value: row.answers[qId], label: row.answers[qId] }
                          : null
                      }
                      onChange={(option) => {
                        const updatedRows = [...exchangeConfigRows];
                        const currentAnswers = updatedRows[rowIndex].answers || {};
                        currentAnswers[qId] = option ? option.value : '';
                        updatedRows[rowIndex].answers = currentAnswers;
                        setExchangeConfigRows(updatedRows);
                      }}
                      placeholder="Select"
                    />
                  </Col>
                ))}
                <Col>
                  <Form.Control
                    type="text"
                    placeholder="Value"
                    value={row.exchangeValue}
                    onChange={(e) =>
                      updateExchangeConfigRow(rowIndex, 'exchangeValue', e.target.value)
                    }
                    style={getInputStyle(errors[`exchangeRow${rowIndex}_exchangeValue`])}
                  />
                  {errors[`exchangeRow${rowIndex}_exchangeValue`] && (
                    <Form.Text style={{ color: 'red' }}>
                      {errors[`exchangeRow${rowIndex}_exchangeValue`]}
                    </Form.Text>
                  )}
                </Col>
                <Col>
                  <Form.Control
                    type="text"
                    placeholder="Bonus"
                    value={row.exchangeBonus}
                    onChange={(e) =>
                      updateExchangeConfigRow(rowIndex, 'exchangeBonus', e.target.value)
                    }
                    style={getInputStyle(false)}
                  />
                </Col>
                <Col>
                  <Select
                    isClearable={true}
                    options={availableBuyBackSkus}
                    value={
                      row.buyBackSku
                        ? availableBuyBackSkus.find((sku) => sku.value === row.buyBackSku)
                        : null
                    }
                    onChange={(option) =>
                      updateExchangeConfigRow(rowIndex, 'buyBackSku', option ? option.value : '')
                    }
                    placeholder="Select BuyBack SKU"
                    styles={{
                      control: (base) => ({
                        ...base,
                        ...getInputStyle(errors[`exchangeRow${rowIndex}_buyBackSku`])
                      })
                    }}
                  />
                  {errors[`exchangeRow${rowIndex}_buyBackSku`] && (
                    <Form.Text style={{ color: 'red' }}>
                      {errors[`exchangeRow${rowIndex}_buyBackSku`]}
                    </Form.Text>
                  )}
                </Col>
                <Col xs="auto">
                  {exchangeConfigRows.length > 1 && (
                    <Button
                      variant="light"
                      onClick={() =>
                        setExchangeConfigRows(exchangeConfigRows.filter((_, i) => i !== rowIndex))
                      }
                      style={{ marginTop: '4px' }}
                    >
                      <X size={16} />
                    </Button>
                  )}
                </Col>
              </Row>
            ))}
            <div style={{width : "100%",display:"flex", justifyContent:"end"}}>
            <Button
              variant="light"
              onClick={() =>
                setExchangeConfigRows([
                  ...exchangeConfigRows,
                  { answers: {}, exchangeValue: '', exchangeBonus: '', buyBackSku: '' }
                ])
              }
              style={{ border: '0px', width: '150px' , borderRadius:"0px" }}
            >
              <Plus size={16} className="me-1" /> Add More
            </Button>
            </div>
          </div>
        </Row>
      )}

      {/* NEW: Excel Upload Section for Exchange Bonus */}
      <Row style={{ backgroundColor: '#ffffff', marginBottom: '16px', padding: '5px' }}>
        <div style={leftStyle}>Exchange Bonus Upload</div>
        <div style={rightStyle}>
          <Form.Group style={{ marginBottom: '12px' }}>
            <Form.Label>Upload Excel File (Columns: vanNo, exchangeBonus)</Form.Label>
            <div  style={{display:"flex",justifyContent:"space-between",alignItems:"center",paddingInline:"5px"}}>
            <Form.Control
              type="file"
              accept=".xls,.xlsx"
              onChange={handleFileUpload}
              style={{...getInputStyle(false),width:"30%"}}
            />
              
            <ExportButton data={excelData}  columns={[{field : "vanNo",key:"vanNo"},{field : "exchangeBonus",key:"exchangeBonus"}]}  fileName = {templateName} style={{ border: '1px solid #EDF0F4',backgroundColor:"#EDF0F4", width: '100px' , borderRadius:"0px",display:"flex", alignItems:"center" , justifyContent:"center" }}/>
            </div>
          </Form.Group>
          {/* VanNo Search input */}
          <Form.Group style={{ marginBottom: '12px' }}>
            <Form.Label>Search Van No</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter vanNo to search"
              value={vanSearch}
              onChange={(e) => setVanSearch(e.target.value)}
              style={getInputStyle(false)}
            />
          </Form.Group>
          {filteredExcelData && filteredExcelData.length > 0 && (
            <>
              <h6>Uploaded Data Preview</h6>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ border: '1px solid #dee2e6', padding: '8px' }}>Van No</th>
                    <th style={{ border: '1px solid #dee2e6', padding: '8px' }}>Exchange Bonus</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredExcelData.slice(0, 10).map((row, index) => (
                    <tr key={index}>
                      <td style={{ border: '1px solid #dee2e6', padding: '8px' }}>{row.vanNo}</td>
                      <td style={{ border: '1px solid #dee2e6', padding: '8px' }}>
                        <ExcelBonusInput
                          value={row.exchangeBonus}
                          row={row}
                          onValueChange={(changedRow, newValue) => {
                            setExcelData((prevData) =>
                              prevData.map((item) =>
                                item.vanNo === changedRow.vanNo
                                  ? { 
                                      ...item, 
                                      exchangeBonus: newValue,
                                      edited: newValue !== item.originalExchangeBonus
                                    }
                                  : item
                              )
                            );
                          }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredExcelData.length > 10 && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button variant="light" onClick={() => setShowExcelModal(true) } style={{ border: '0px', width: '200px' , borderRadius:"0px" }}>
                    Show All
                  </Button>
                </div>
              
              )}
            </>
          )}
        </div>
      </Row>

      {/* Assignment Details Section (only during creation) */}
      <Row style={{ backgroundColor: '#ffffff', marginBottom: '16px', padding: '5px' }}>
        <div style={leftStyle}>Assignment Details</div>
        <div style={rightStyle}>
          <Form.Group style={{ marginBottom: '12px' }}>
            <div>
              <Form.Label>CATEGORY</Form.Label>
              <div>
                <EditableDropDown
                  id="category"
                  onChange={(e) => setCategory(e.map((option) => option.value))}
                  style={{ height: 38, width: '100%' }}
                  isMultiSelect={true}
                  value={category}
                  dropdownBackgroundColor="light"
                  options={arrayToDropdownOptions(categories)}
                  placeholder="Select Category"
                />
              </div>
            </div>
          </Form.Group>
          <Form.Group style={{ marginBottom: '12px' }}>
            <div>
              <Form.Label>BRAND</Form.Label>
              <EditableDropDown
                id="brand"
                onChange={(e) => setBrand(e.map((option) => option.value))}
                style={{ height: 38, width: '100%' }}
                isMultiSelect={true}
                dropdownBackgroundColor="light"
                value={brand}
                options={arrayToDropdownOptions(companies)}
                placeholder="Select Brand"
              />
            </div>
          </Form.Group>
          <Form.Group style={{ marginBottom: '12px' }}>
            <div>
              <Form.Label>VAN NO.</Form.Label>
              <AsyncDropdown
                onChange={(option) => setVanNo(option || [])}
                style={{ height: 38, width: '100%' }}
                isMultiSelect={true}
                value={vanNo}
                backgroundColor="#FFF"
                optionColor="rgba(240, 240, 240, 1)"
                disabled={false}
                fetchOptions={(e) => fetchOptions(e, vanNoOptions)}
                placeholder={t('Select vanNo')}
              />
            </div>
          </Form.Group>
        </div>
      </Row>

      {/* Bottom Save/Cancel Buttons */}
      <Row style={{ backgroundColor: '#ffffff', marginBottom: '16px', padding: '5px' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px', width: '100%' }}>
          <Button variant="light" onClick={handleCancel} style={{ width: '200px', border: '0px' }}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} style={{ width: '200px', border: '0px' }}>
            {isEditMode ? 'Update' : 'Save'}
          </Button>
        </div>
      </Row>

      <ConfirmationModal
        showModal={showConfirm}
        onConfirm={saveTemplate}
        closeModal={() => setShowConfirm(false)}
        modalBody={
          isEditMode
            ? 'This template will be updated with the provided details. Are you sure you want to proceed?'
            : isCloning
            ? 'A new cloned template will be created. Are you sure you want to proceed?'
            : 'This template will be created and assigned. Are you sure you want to proceed?'
        }
        modalTitle={t(
          isCloning ? 'Clone Template' : isEditMode ? 'Update Template' : 'Create Template'
        )}
        confirmationModalProps={[]}
        setConfirmationModalProps={() => {}}
      />

      <InfoModal
        showModal={showExcelModal}
        closeModal={() => setShowExcelModal(false)}
        modalTitle="All Exchange Bonus"
        data={filteredExcelData}
        columns={[
          {
            dataField: 'vanNo',
            text: 'Van No'
          },
          {
            dataField: 'exchangeBonus',
            text: 'Exchange Bonus',
            formatter: (cell, row) => (
              <ExcelBonusInput
                value={cell}
                row={row}
                onValueChange={(changedRow, newValue) => {
                  setExcelData((prevData) =>
                    prevData.map((item) =>
                      item.vanNo === changedRow.vanNo
                        ? { 
                            ...item, 
                            exchangeBonus: newValue,
                            edited: newValue !== item.originalExchangeBonus
                          }
                        : item
                    )
                  );
                }}
              />
            )
          }
        ]}
        size="xl"
      />
    </Container>
  );
};

export default CreateNewTemplate;
